import React from "react";

// Phase 3D.2-C - Presentational card for ONE detected growth opportunity.
//
// PURE PRESENTATION ONLY:
//   * Renders EXCLUSIVELY the fields returned by selectGrowthOpportunity(dataset).
//   * NO KPI recalculation, NO API call, NO hardcoded market/figure.
//   * NO marketing recommendation / campaign / budget / channel / audience / projection.
//   * Rounding here is purely visual; the engine already filtered/ranked on raw values.
//   * Average stay & spend are shown as CONTEXT ONLY (never a selection reason).

import ProvenanceBadges from "@/components/provenance/ProvenanceBadges";
import { MISSING, periodLabel } from "@/lib/savoieKpis";

// --- Visual-only formatters (fr-FR) --------------------------------------
const nf = new Intl.NumberFormat("fr-FR");
const fmtInt = (v) => (typeof v === "number" ? nf.format(Math.round(v)) : MISSING);
const fmtPct1 = (v) =>
  typeof v === "number" ? `${(Math.round(v * 10) / 10).toFixed(1).replace(".", ",")} %` : MISSING;
const fmtPctSigned = (v) =>
  typeof v === "number"
    ? `${v >= 0 ? "+" : ""}${(Math.round(v * 10) / 10).toFixed(1).replace(".", ",")} %`
    : MISSING;
const fmtStay = (v) =>
  typeof v === "number" ? `${(Math.round(v * 10) / 10).toFixed(1).replace(".", ",")} nuits` : MISSING;
// Spend shows the currency ONLY when it is explicitly EUR (per phase rule).
const fmtSpend = (v, currency) => {
  if (typeof v !== "number") return MISSING;
  const n = fmtInt(v);
  return currency === "EUR" ? `${n} EUR` : n;
};

// "2024-12" -> "décembre 2024" (reuses the pure label helper; no KPI recompute).
function frPeriod(iso) {
  if (typeof iso !== "string") return MISSING;
  const [y, m] = iso.split("-").map((x) => Number(x));
  if (!Number.isFinite(y) || !Number.isFinite(m)) return MISSING;
  return periodLabel({ year: y, month: m });
}

function DataRow({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-slate-100 py-1.5 first:border-t-0">
      <dt className="text-sm text-slate-600">{label}</dt>
      <dd className="text-sm font-semibold text-slate-900" data-testid={testid}>{value}</dd>
    </div>
  );
}

export default function OpportunityCard({ opportunity }) {
  if (!opportunity || opportunity.status !== "detected") return null;

  const { evidence, context, thresholds } = opportunity;

  return (
    <section
      className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 shadow-sm"
      aria-labelledby="opportunity-title"
      data-testid="opportunity-card"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2
          id="opportunity-title"
          className="font-heading text-lg font-bold tracking-tight text-emerald-900 sm:text-xl"
          data-testid="opportunity-title"
        >
          Opportunité de croissance détectée
        </h2>
        {/* Demo badge from explicit provenance; no Simulation badge (simulation === false). */}
        <ProvenanceBadges meta={opportunity.meta} />
      </div>

      <p className="mt-3 text-sm leading-relaxed text-slate-800" data-testid="opportunity-statement">
        {opportunity.user_facing_text}
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* ZONE 1 — Evidence that drove the selection */}
        <div
          className="rounded-xl border border-emerald-200 bg-white p-4"
          data-testid="opportunity-evidence"
        >
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">
            Preuves ayant déterminé la sélection
          </h3>
          <dl>
            <DataRow label="Marché" value={opportunity.market_name} testid="ev-market" />
            <DataRow label="Période analysée" value={frPeriod(opportunity.period)} testid="ev-period" />
            <DataRow
              label="Période de comparaison"
              value={frPeriod(opportunity.comparison_period)}
              testid="ev-comparison"
            />
            <DataRow
              label="Croissance des arrivées"
              value={fmtPctSigned(evidence.growth_percent)}
              testid="ev-growth"
            />
            <DataRow
              label="Arrivées visiteurs"
              value={fmtInt(evidence.visitor_arrivals)}
              testid="ev-arrivals"
            />
            <DataRow
              label="Part du marché"
              value={fmtPct1(evidence.market_share_percent)}
              testid="ev-share"
            />
          </dl>
          <p className="mt-2 text-xs text-slate-500" data-testid="opportunity-thresholds">
            Critères du pilote satisfaits : croissance ≥ {fmtPct1(thresholds.min_growth_percent)} et
            arrivées ≥ {fmtInt(thresholds.min_visitor_arrivals)} visiteurs.
          </p>
        </div>

        {/* ZONE 2 — Context, explicitly NOT used for selection */}
        <div
          className="rounded-xl border border-slate-200 bg-white p-4"
          data-testid="opportunity-context"
        >
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Contexte — non utilisé pour la sélection
          </h3>
          <dl>
            <DataRow
              label="Durée moyenne de séjour"
              value={fmtStay(context.average_stay)}
              testid="ctx-stay"
            />
            <DataRow
              label="Dépense moyenne / visiteur"
              value={fmtSpend(context.tourism_spend_per_visitor, context.currency)}
              testid="ctx-spend"
            />
          </dl>
          <p className="mt-2 text-xs italic text-slate-500" data-testid="opportunity-context-note">
            {"Ces indicateurs de contexte n'influencent pas la sélection de l'opportunité."}
          </p>
        </div>
      </div>

      <p className="mt-4 text-xs text-slate-500" data-testid="opportunity-disclaimer">
        {opportunity.disclaimer}
      </p>
    </section>
  );
}
